import { useState } from 'react';
import { AIProject, AIProjectReview } from '../../../../mocks/aiProjects';
import { useAuth } from '../../../../contexts/AuthContext';
import { AuthModal } from '../../../../components/feature/AuthModal';

interface ProjectRatingsProps {
  project: AIProject;
  reviews: AIProjectReview[];
}

const ProjectRatings = ({ project, reviews }: ProjectRatingsProps) => {
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<'newest' | 'helpful' | 'highest' | 'lowest'>('helpful');
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [categoryRatings, setCategoryRatings] = useState({
    visuals: 0,
    story: 0,
    audio: 0,
    creativity: 0,
  });
  const [reviewTitle, setReviewTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [submittedReview, setSubmittedReview] = useState<AIProjectReview | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const sortedReviews = [...reviews].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.date).getTime() - new Date(a.date).getTime();
      case 'helpful':
        return b.helpful - a.helpful;
      case 'highest':
        return b.rating - a.rating;
      case 'lowest':
        return a.rating - b.rating;
      default:
        return 0;
    }
  });

  const allReviews = submittedReview ? [submittedReview, ...sortedReviews] : sortedReviews;

  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    percentage: reviews.length > 0 ? (reviews.filter(r => r.rating === star).length / reviews.length) * 100 : 0,
  }));

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md', interactive = false) => {
    const sizeClasses = {
      sm: 'text-sm',
      md: 'text-lg',
      lg: 'text-2xl',
    };

    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setUserRating(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'} transition-colors`}
          >
            <i
              className={`${
                star <= (interactive ? (hoverRating || userRating) : rating)
                  ? 'ri-star-fill text-amber-400'
                  : 'ri-star-line text-slate-600'
              } ${sizeClasses[size]}`}
            ></i>
          </button>
        ))}
      </div>
    );
  };

  const renderCategoryRating = (label: string, key: keyof typeof categoryRatings, value: number, interactive = false) => (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-400 w-24">{label}</span>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setCategoryRatings(prev => ({ ...prev, [key]: star }))}
            className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          >
            <i
              className={`${
                star <= (interactive ? categoryRatings[key] : value)
                  ? 'ri-star-fill text-amber-400'
                  : 'ri-star-line text-slate-600'
              } text-sm`}
            ></i>
          </button>
        ))}
        <span className="text-sm text-slate-300 ml-2 w-8">{interactive ? categoryRatings[key] || '-' : value.toFixed(1)}</span>
      </div>
    </div>
  );

  const handleWriteReviewClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setShowReviewForm(true);
  };

  const handleSubmitReview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (userRating === 0 || !reviewTitle.trim() || !reviewText.trim()) return;

    const newReview: AIProjectReview = {
      id: Date.now(),
      user: 'You',
      avatar: 'https://public.readdy.ai/ai/img_res/6fecbfbdeccb76e591cf0316516107fd.jpg',
      rating: userRating,
      title: reviewTitle,
      review: reviewText,
      date: new Date().toISOString().split('T')[0],
      helpful: 0,
      categories: categoryRatings.visuals > 0 ? categoryRatings : { visuals: userRating, story: userRating, audio: userRating, creativity: userRating },
    };

    setSubmittedReview(newReview);
    setShowReviewForm(false);
    setUserRating(0);
    setCategoryRatings({ visuals: 0, story: 0, audio: 0, creativity: 0 });
    setReviewTitle('');
    setReviewText('');
  };

  return (
    <div className="space-y-6">
      {/* Rating Overview */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Overall Score */}
          <div className="flex flex-col items-center justify-center text-center md:border-r md:border-slate-700 md:pr-8">
            <div className="text-5xl font-bold text-white mb-2">
              {project.averageRating?.toFixed(1) || '0.0'}
            </div>
            {renderStars(project.averageRating || 0, 'lg')}
            <p className="text-slate-400 text-sm mt-2">
              {project.totalReviews || 0} {(project.totalReviews || 0) === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          {/* Rating Distribution */}
          <div className="flex-1 space-y-2">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Rating Distribution</h3>
            {ratingDistribution.map(({ star, count, percentage }) => (
              <div key={star} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-16">
                  <span className="text-sm text-slate-400">{star}</span>
                  <i className="ri-star-fill text-amber-400 text-sm"></i>
                </div>
                <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <span className="text-sm text-slate-400 w-8">{count}</span>
              </div>
            ))}
          </div>

          {/* Category Breakdown */}
          <div className="md:border-l md:border-slate-700 md:pl-8 space-y-3">
            <h3 className="text-sm font-semibold text-slate-300 mb-3">Category Scores</h3>
            {renderCategoryRating('Visuals', 'visuals', project.ratingBreakdown?.visuals || 0)}
            {renderCategoryRating('Story', 'story', project.ratingBreakdown?.story || 0)}
            {renderCategoryRating('Audio', 'audio', project.ratingBreakdown?.audio || 0)}
            {renderCategoryRating('Creativity', 'creativity', project.ratingBreakdown?.creativity || 0)}
          </div>
        </div>
      </div>

      {/* Write Review Button */}
      {!showReviewForm && !submittedReview && (
        <button
          onClick={handleWriteReviewClick}
          className="w-full py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-rose-600 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
        >
          <i className={user ? 'ri-edit-line' : 'ri-lock-line'}></i>
          {user ? 'Write a Review' : 'Sign In to Write a Review'}
        </button>
      )}

      {/* Review Form */}
      {showReviewForm && user && (
        <form onSubmit={handleSubmitReview} className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 space-y-5">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <i className="ri-edit-line text-orange-400"></i>
            Write Your Review
          </h3>

          {/* Overall Rating */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Overall Rating *</label>
            <div className="flex items-center gap-3">
              {renderStars(userRating, 'lg', true)}
              {userRating > 0 && (
                <span className="text-slate-400 text-sm">
                  {userRating === 5 ? 'Excellent' : userRating === 4 ? 'Great' : userRating === 3 ? 'Good' : userRating === 2 ? 'Fair' : 'Poor'}
                </span>
              )}
            </div>
          </div>

          {/* Category Ratings */}
          <div className="bg-slate-700/30 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium text-slate-300 mb-2">Rate by Category (Optional)</h4>
            {renderCategoryRating('Visuals', 'visuals', 0, true)}
            {renderCategoryRating('Story', 'story', 0, true)}
            {renderCategoryRating('Audio', 'audio', 0, true)}
            {renderCategoryRating('Creativity', 'creativity', 0, true)}
          </div>

          {/* Review Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Review Title *</label>
            <input
              type="text"
              value={reviewTitle}
              onChange={(e) => setReviewTitle(e.target.value)}
              placeholder="Summarize your experience"
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 text-sm"
              maxLength={100}
            />
          </div>

          {/* Review Text */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Your Review *</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value.slice(0, 500))}
              placeholder="Share your thoughts about this project..."
              rows={4}
              className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-orange-500 text-sm resize-none"
              maxLength={500}
            ></textarea>
            <p className="text-xs text-slate-500 mt-1">{reviewText.length}/500 characters</p>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowReviewForm(false)}
              className="flex-1 py-3 bg-slate-700 text-slate-300 font-medium rounded-lg hover:bg-slate-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={userRating === 0 || !reviewTitle.trim() || !reviewText.trim()}
              className="flex-1 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-rose-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              Submit Review
            </button>
          </div>
        </form>
      )}

      {/* Reviews List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">
            Reviews ({allReviews.length})
          </h3>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-slate-300 focus:outline-none focus:border-orange-500 cursor-pointer"
          >
            <option value="helpful">Most Helpful</option>
            <option value="newest">Newest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
          </select>
        </div>

        {allReviews.length === 0 ? (
          <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-slate-700/50 rounded-full">
              <i className="ri-chat-smile-3-line text-3xl text-slate-500"></i>
            </div>
            <h4 className="text-white font-medium mb-2">No Reviews Yet</h4>
            <p className="text-slate-400 text-sm">Be the first to review this project!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {allReviews.map((review) => (
              <div
                key={review.id}
                className={`bg-slate-800/50 rounded-xl border ${review.user === 'You' ? 'border-orange-500/50' : 'border-slate-700'} p-5`}
              >
                <div className="flex items-start gap-4">
                  <img
                    src={review.avatar}
                    alt={review.user}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-white">{review.user}</span>
                          {review.user === 'You' && (
                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">Your Review</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {renderStars(review.rating, 'sm')}
                          <span className="text-slate-500 text-xs">{review.date}</span>
                        </div>
                      </div>
                    </div>

                    <h4 className="font-semibold text-white mb-2">{review.title}</h4>
                    <p className="text-slate-300 text-sm leading-relaxed mb-4">{review.review}</p>

                    {/* Category Ratings */}
                    <div className="flex flex-wrap gap-4 mb-4">
                      {Object.entries(review.categories).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-1.5">
                          <span className="text-xs text-slate-500 capitalize">{key}:</span>
                          <div className="flex items-center">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <i
                                key={star}
                                className={`${star <= value ? 'ri-star-fill text-amber-400' : 'ri-star-line text-slate-600'} text-xs`}
                              ></i>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Helpful Button */}
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => {
                          if (!user) {
                            setShowAuthModal(true);
                            return;
                          }
                          // TODO: helpful logic
                        }}
                        className="flex items-center gap-1.5 text-slate-400 hover:text-orange-400 transition-colors text-sm cursor-pointer"
                      >
                        <i className="ri-thumb-up-line"></i>
                        <span>Helpful ({review.helpful})</span>
                      </button>
                      <button
                        onClick={() => {
                          if (!user) {
                            setShowAuthModal(true);
                            return;
                          }
                          // TODO: report logic
                        }}
                        className="flex items-center gap-1.5 text-slate-400 hover:text-slate-300 transition-colors text-sm cursor-pointer"
                      >
                        <i className="ri-flag-line"></i>
                        <span>Report</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default ProjectRatings;
